import React from "react";
import { Collapse, Switch, Divider } from "antd";
import { SafetyOutlined } from "@ant-design/icons";
import { FeatureData } from "../../../Types";

interface FeaturePermissionsProps {
  subFeatures: FeatureData[];
  overrides: Record<string, number>;
  onOverridesChange: (overrides: Record<string, number>) => void;
  isCreating: boolean;
}

const FeaturePermissions: React.FC<FeaturePermissionsProps> = ({
  subFeatures,
  overrides,
  onOverridesChange,
  isCreating,
}) => {
  const toggleOverride = (subFeatureId: string, checked: boolean) => {
    onOverridesChange({
      ...overrides,
      [subFeatureId]: checked ? 1 : 0,
    });
  };

  return (
    <>
      <Divider className="my-4" />
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <SafetyOutlined className="text-gray-500" />
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Feature Permissions
          </span>
          <span className="text-xs text-red-400 ml-1">*</span>
        </div>
        <p className="text-xs text-gray-400 mb-2">
          {isCreating
            ? "Set feature permissions for this user (denied sub-features will be hidden)."
            : "Override role-based permissions for this user (denied sub-features will be hidden)."}
        </p>
        <Collapse
          size="small"
          ghost
          defaultActiveKey={subFeatures
            .filter((f) =>
              f.sub_features?.some(
                (sf) => overrides[sf.id] !== undefined,
              ),
            )
            .map((f) => f.feature_code)}
        >
          {subFeatures.map((feature) => (
            <Collapse.Panel
              key={feature.feature_code}
              header={
                <span className="text-sm font-medium text-gray-700">
                  {feature.feature_name}
                </span>
              }
            >
              <div className="flex flex-col gap-1">
                {(feature.sub_features ?? []).map((sf) => {
                  const isOverridden =
                    overrides[sf.id] !== undefined;
                  const allowed = overrides[sf.id] ?? 1;
                  return (
                    <div
                      key={sf.id}
                      className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          {sf.name}
                        </span>
                        {isOverridden && (
                          <span className="text-[10px] text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded">
                            Override
                          </span>
                        )}
                      </div>
                      <Switch
                        size="small"
                        checked={allowed === 1}
                        onChange={(checked) =>
                          toggleOverride(sf.id, checked)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </Collapse.Panel>
          ))}
        </Collapse>
      </div>
    </>
  );
};

export default FeaturePermissions;
